import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import type {
  DeviceMode,
  DevicePlatform,
  ICreateDeviceRequest,
} from '@/feature/device/types/device';
import FormCheckbox from '@/shared/components/forms/FormCheckbox';
import FormInput from '@/shared/components/forms/FormInput';
import FormSelect from '@/shared/components/forms/FormSelect';
import Button from '@/shared/components/ui/Button';
import Modal from '@/shared/components/ui/Modals/Modal';

interface IAddDeviceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (data: ICreateDeviceRequest) => Promise<boolean>;
}

interface IAddDeviceForm {
  device_name: string;
  device_id: string;
  platform: DevicePlatform | '';
  device_mode: DeviceMode | '';
  vnc_url: string;
  appium_url: string;
  real: boolean;
}

const emptyForm: IAddDeviceForm = {
  device_name: '',
  device_id: '',
  platform: '',
  device_mode: '',
  vnc_url: '',
  appium_url: '',
  real: false,
};

const compactField = '!rounded-md !py-2 !text-sm border-gray-300';
const labelCompact = 'text-sm font-medium text-gray-700 mb-2';

const AddDeviceModal = ({ isOpen, onClose, onAdd }: IAddDeviceModalProps) => {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<IAddDeviceForm>({ defaultValues: emptyForm });

  const platform = watch('platform');
  const deviceMode = watch('device_mode');
  const real = watch('real');

  useEffect(() => {
    if (isOpen) {
      reset(emptyForm);
    }
  }, [isOpen, reset]);

  const modeOptions = useMemo(() => {
    const options: { value: DeviceMode; label: string }[] = [];
    if (platform === 'android' || platform === '') {
      options.push({ value: 'emulator', label: 'Emulator' });
    }
    if (platform === 'ios' || platform === '') {
      options.push({ value: 'simulator', label: 'Simulator' });
    }
    options.push({ value: 'real_device', label: 'Real Device' });
    return options;
  }, [platform]);

  const onValid = async (data: IAddDeviceForm) => {
    const payload: ICreateDeviceRequest = {
      device_name: data.device_name.trim(),
      device_id: data.device_id.trim(),
      platform: data.platform as DevicePlatform,
      device_mode: data.device_mode as DeviceMode,
      vnc_url: data.vnc_url.trim(),
      appium_url: data.appium_url.trim(),
      real: data.real,
    };
    const ok = await onAdd(payload);
    if (ok) {
      reset(emptyForm);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title='Add Device' size='lg'>
      <form onSubmit={handleSubmit(onValid)} className='p-6'>
        <p className='text-sm text-gray-500 mb-5'>Register a new device into the inventory.</p>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <FormInput
            name='device_name'
            label='Device Name'
            labelClassName={labelCompact}
            placeholder='e.g. Samsung Galaxy S24'
            register={register}
            errors={errors}
            validation={{ required: 'Device name is required' }}
            className={compactField}
          />
          <FormInput
            name='device_id'
            label='Device ID'
            labelClassName={labelCompact}
            placeholder='e.g. DEV-1008'
            register={register}
            errors={errors}
            validation={{ required: 'Device ID is required' }}
            className={compactField}
          />
        </div>
        <div className='mt-4'>
          <FormCheckbox
            checked={real}
            onChange={(checked) => setValue('real', checked)}
            label='Real Device'
            description='Indicates whether this is a physical device'
            variant='switch'
          />
        </div>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mt-4'>
          <FormSelect
            name='platform'
            label='Device Type'
            options={[
              { value: 'android', label: 'Android' },
              { value: 'ios', label: 'iOS' },
            ]}
            register={register}
            errors={errors}
            value={platform}
            onChange={(value) => {
              const next = value as DevicePlatform;
              setValue('platform', next, { shouldValidate: true });
              if (next === 'android' && deviceMode === 'simulator') {
                setValue('device_mode', '');
              }
              if (next === 'ios' && deviceMode === 'emulator') {
                setValue('device_mode', '');
              }
            }}
            validation={{ required: 'Device type is required' }}
            className={compactField}
          />
          <FormSelect
            name='device_mode'
            label='Device Mode'
            options={modeOptions}
            register={register}
            errors={errors}
            value={deviceMode}
            onChange={(value) => {
              const next = value as DeviceMode;
              setValue('device_mode', next, { shouldValidate: true });
              setValue('real', next === 'real_device');
            }}
            validation={{ required: 'Device mode is required' }}
            className={compactField}
          />
          <FormInput
            name='vnc_url'
            label='VNC URL'
            labelClassName={labelCompact}
            placeholder='https://vnc.local/DEV-1008'
            register={register}
            errors={errors}
            validation={{ required: 'VNC URL is required' }}
            className={compactField}
          />
          <FormInput
            name='appium_url'
            label='Appium URL'
            labelClassName={labelCompact}
            placeholder='http://127.0.0.1:4723/DEV-1008'
            register={register}
            errors={errors}
            validation={{ required: 'Appium URL is required' }}
            className={compactField}
          />
        </div>
        <div className='flex justify-end gap-3 mt-6'>
          <Button type='button' variant='secondary' onClick={onClose}>
            Cancel
          </Button>
          <Button type='submit' variant='primary' isLoading={isSubmitting}>
            Add Device
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddDeviceModal;
